/**
 * ServiceCategoryDetail — 서비스 카테고리 상세 항목 리스트 (2단계)
 *
 * 비로그인 사용자가 로그인 필수 항목(🔒)을 클릭하면 onSelectLockedItem으로
 * 로그인 유도 메시지를 발생시킨다.
 */

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import type { ServiceCategory, ServiceItem } from '@/types/qa';

interface ServiceCategoryDetailProps {
  category: ServiceCategory;
  onSelectIntent: (intent: string, label: string) => void;
  onSelectLockedItem: (item: ServiceItem) => void;
  onTriggerQuoteForm: () => void;
  onBack: () => void;
}

const QUOTE_FORM_TRIGGER = 'quote_form_trigger';

export function ServiceCategoryDetail({
  category,
  onSelectIntent,
  onSelectLockedItem,
  onTriggerQuoteForm,
  onBack,
}: ServiceCategoryDetailProps) {
  const authStatus = useAuthStore((s) => s.status);

  return (
    <div className="ml-flex ml-flex-col ml-gap-2 ml-mt-2">
      <div className="ml-flex ml-flex-col ml-bg-white ml-border ml-border-line ml-rounded-cb-md ml-overflow-hidden">
        {category.items.map((item, idx) => {
          const isLocked =
            item.auth_required === 'required' && authStatus !== 'authenticated';

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (isLocked) {
                  onSelectLockedItem(item);
                } else if (item.type === 'intent' && item.intent) {
                  onSelectIntent(item.intent, item.label);
                } else if (
                  item.type === 'submenu' &&
                  item.submenu_id === QUOTE_FORM_TRIGGER
                ) {
                  onTriggerQuoteForm();
                }
              }}
              className={cn(
                'ml-flex ml-items-center ml-gap-2',
                'ml-px-3 ml-py-2.5 ml-text-left',
                'ml-text-cb-base ml-cursor-pointer',
                'ml-transition-colors ml-duration-150',
                idx > 0 && 'ml-border-t ml-border-line-soft',
                isLocked
                  ? 'ml-text-mute hover:ml-bg-surface'
                  : 'ml-text-ink-secondary hover:ml-bg-brand-50 hover:ml-text-brand-600',
                item.highlight &&
                  !isLocked &&
                  'ml-bg-brand-50/50 ml-text-brand-600 ml-font-medium'
              )}
            >
              <span className="ml-text-mute ml-text-cb-sm">▸</span>
              <span className="ml-flex-1 ml-min-w-0 ml-truncate">{item.label}</span>
              {isLocked && (
                <span className="ml-text-cb-xs" title="로그인 필요">
                  🔒
                </span>
              )}
              {item.highlight && !isLocked && (
                <span className="ml-text-cb-xs ml-text-accent">⭐</span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onBack}
        className={cn(
          'ml-self-start ml-flex ml-items-center ml-gap-1.5',
          'ml-px-3 ml-py-1.5 ml-rounded-cb-sm',
          'ml-text-cb-sm ml-text-mute ml-cursor-pointer',
          'hover:ml-bg-surface hover:ml-text-ink ml-transition-colors'
        )}
      >
        <span>←</span>
        <span>서비스 메뉴로</span>
      </button>
    </div>
  );
}
